import { ChevronRightIcon, ExternalLinkIcon } from "@heroicons/react/outline";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { logEvent } from "@firebase/analytics";
import { analytics } from "../../firebase";
import {
  requestGetConsultation,
  useAuthState,
  useConsultationDispatch,
  useConsultationState,
} from "../../Context";
import { Alert } from "../../Components/Alert";
import MaintenanceModal from "../../Components/MaintenanceModal";
import { useRemoteConfig } from "../../Context/firebase/context";

const yearOptions = [
  { label: "year2024AndEarlierLabel", value: "2024_AND_EARLIER" },
  { label: "year2025AndLaterLabel", value: "2025_AND_LATER" },
];

const navigationEbillings = [
  {
    label: "inputCodeBillingLabel",
    value: "inputCodeBilling",
  },
  {
    label: "PPh 21",
    value: "411121",
    kjs: "100",
  },
  {
    label: "PPh Final",
    value: "411128",
    kjs: "420",
  },
  {
    label: "PPN",
    value: "411211",
    kjs: "100",
  },
  {
    label: "PPh 23",
    value: "411124",
    kjs: "100",
  },
  {
    label: "PPh Pasal 25 Orang Pribadi",
    value: "411125",
    kjs: "100",
  },
  {
    label: "PPh Pasal 25/29 Badan",
    value: "411126",
  },
  {
    label: "Pajak Pusat Lainnya",
    value: "OTHER",
  },
];

export const EbillingTemplate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useConsultationDispatch();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const consultationIdParam = searchParams.get("consultation_id");
  const { consultation } = useConsultationState();
  const { user } = useAuthState();
  const { ebilling_create: ebillingCreateConfig, loading: configLoading } =
    useRemoteConfig();

  const [ebillingCreateEnabled, setebillingCreateEnabled] = useState(true);
  const [
    ebillingCreateMaintenanceVisible,
    setebillingCreateMaintenanceVisible,
  ] = useState(false);
  const [selectedYear, setSelectedYear] = useState("2025_AND_LATER");

  useEffect(() => {
    if (consultationIdParam && user.membership === "CONSULTANT") {
      requestGetConsultation(
        dispatch,
        {
          id: consultationIdParam,
        },
        true
      );
    }
  }, [dispatch, consultationIdParam, user.membership]);

  const handleClick = (item) => {
    if (item.isExternal) {
      logEvent(analytics, "select_content", {
        content_type: "ebilling",
        content_id: item.value,
      });
      window.open("https://coretaxdjp.pajak.go.id/", "_blank", "noopener,noreferrer");
      return;
    }
    
    if (!ebillingCreateEnabled) {
      setebillingCreateMaintenanceVisible(true);
    } else {
      // Analytics
      logEvent(analytics, "select_content", {
        content_type: "ebilling",
        content_id: item.value,
      });
      // Navigate
      if (item.value === "inputCodeBilling") {
        navigate("/ebilling/input");
      } else if (item.value === "OTHER") {
        navigate(
          `/ebilling/create${
            consultation.id ? `?consultation_id=${consultation.id}` : ""
          }`
        );
      } else {
        navigate(
          `/ebilling/create?${
            consultation.id ? `consultation_id=${consultation.id}` : ""
          }&kap=${item.value}${item.kjs ? `&kjs=${item.kjs}` : ""}`
        );
      }
    }
  };

  const EbillingTemplateSkeleton = () => {
    return (
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="flex flex-col divide-y divide-gray-200">
          {Array.from({ length: 4 }).map((_, index) => (
            <li key={index} className="flex justify-between p-4 animate-pulse">
              <div className="flex min-w-0 space-x-4 w-full">
                <div className="h-4 bg-gray-200 rounded w-1/4"></div>
              </div>
              <div className="hidden shrink-0 sm:flex sm:flex-col sm:items-end">
                <div className="h-5 w-5 bg-gray-200 rounded-full"></div>
              </div>
            </li>
          ))}
        </div>
      </div>
    );
  };

  const isDraftingBilling =
    consultation.id && user.id && user.membership === "CONSULTANT";

  const filteredNavigationItems = useMemo(() => {
    if (!selectedYear) return [];
    
    if (selectedYear === "2025_AND_LATER") {
      return [
        ...navigationEbillings.filter((item) => item.value === "inputCodeBilling"),
        { label: "createBillingCoretaxLabel", value: "coretax", isExternal: true },
      ];
    }
    
    // 2024_AND_EARLIER
    return isDraftingBilling
      ? navigationEbillings.filter((item) => item.value !== "inputCodeBilling")
      : navigationEbillings;
  }, [isDraftingBilling, selectedYear]);

  const handleYearSelect = (yearValue) => {
    setSelectedYear(yearValue);
  };

  if (configLoading) {
    return <EbillingTemplateSkeleton />;
  }

  return (
    <div className="space-y-4">
      {isDraftingBilling && (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden md:w-max">
          <div className="flex flex-col divide-y divide-gray-200">
            <div className="p-4">
              <Alert
                type="info"
                message="Drafting an E-Billing for Consultation"
              />
            </div>
            <div className="flex flex-col p-4">
              <span className="text-sm font-medium text-gray-700">
                {consultation?.description?.name}
              </span>
              <span className="text-sm text-gray-700">
                {consultation?.category?.name}
              </span>
            </div>
          </div>
        </div>
      )}
      <MaintenanceModal
        isVisible={ebillingCreateMaintenanceVisible}
        onClose={() => setebillingCreateMaintenanceVisible(false)}
        onChangeAvailability={(available) =>
          setebillingCreateEnabled(available)
        }
        configValue={ebillingCreateConfig}
      />
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="flex flex-col divide-y divide-gray-200">
          <div className="p-4">
            <p className="text-sm font-medium text-gray-700 mb-3">
              {t("selectYearLabel")}
            </p>
            <div className="flex flex-wrap gap-2">
              {yearOptions.map((option) => (
                <button
                  key={option.value}
                  data-testid={`year-option-${option.value}`}
                  onClick={() => handleYearSelect(option.value)}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    selectedYear === option.value
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {t(option.label)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      {selectedYear && (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="flex flex-col divide-y divide-gray-200">
            {filteredNavigationItems.map((item) => {
              const testId = `ebilling-template-${item.value}`;
              return (
                <button
                  data-testid={testId}
                  key={item.value}
                  onClick={() => handleClick(item)}
                >
                  <li className="flex justify-between p-4">
                    <div className="flex min-w-0">
                      <p className="text-sm font-medium text-gray-700">
                        {t(item.label)}
                      </p>
                    </div>
                    <div className="hidden shrink-0 sm:flex sm:flex-col sm:items-end">
                      {item.isExternal ? (
                        <ExternalLinkIcon className="h-5 w-5 text-gray-500" />
                      ) : (
                        <ChevronRightIcon className="h-5 w-5 text-gray-500" />
                      )}
                    </div>
                  </li>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
